import { useCallback } from "react";
import { setFunnelModalConfig } from "../FunnelModal";
import { FUNNEL_TYPES } from "../utils/contants";
import { useGetNavigationContext } from "../../../hooks/useGetNavigationContext";
import {
  FunnelContextArea,
  FunnelOnSuccessResult,
  FunnelContextTypes,
  FunnelOptionsTypes,
  FunnelConfigTypes,
  EventContextArea,
} from "../types";
import { shouldShowOnboardingAfterAuth } from "../../../utils/shouldShowOnboardingAfterAuth";
import { AuthType } from "../../../hooks/useAuthMutations";
import { useAccountData } from "../../../hooks/useAccountData";

type UseAuthOrchestrationProps = {
  contextArea: FunnelContextArea;
  onComplete?: (result: FunnelOnSuccessResult) => void;
  skipOnboarding?: boolean;
  context?: FunnelContextTypes;
  options?: FunnelOptionsTypes;
  setFunnelConfig?: (config?: FunnelConfigTypes) => void;
};

function shouldDisablePostAuthPromosForE2E(): boolean {
  try {
    return localStorage.getItem("gc_e2e_disable_post_auth_promos") === "1";
  } catch {
    return false;
  }
}

export function useAuthOrchestration({
  contextArea,
  onComplete,
  skipOnboarding = true,
  context,
  options,
  setFunnelConfig = setFunnelModalConfig,
}: UseAuthOrchestrationProps) {
  const accountData = useAccountData();

  // Map FunnelContextArea to EventContextArea for analytics
  const mapToEventContext = (area: FunnelContextArea): EventContextArea => {
    switch (area) {
      case FunnelContextArea.reader:
      case FunnelContextArea.verticalReader:
        return EventContextArea.reader;
      case FunnelContextArea.settings:
        return EventContextArea.settings;
      case FunnelContextArea.gold:
      case FunnelContextArea.gift:
        return EventContextArea.goldPage;
      case FunnelContextArea.offerPopUp:
        return EventContextArea.offerPopUp;
      default:
        return EventContextArea.settings; // Safe default for auth contexts
    }
  };

  const defaultNavigationContext = useGetNavigationContext({
    contextArea: mapToEventContext(contextArea),
  });
  const navigationContext =
    context?.navigationContext || defaultNavigationContext;

  const introOfferNavigationContext = useGetNavigationContext({
    contextArea: EventContextArea.offerPopUp,
  });

  const handleAuthSuccess = useCallback(
    (result: FunnelOnSuccessResult) => {
      const newAccountResponse = result.data?.[0];
      const newAccountData = newAccountResponse?.data?.payload?.results;
      const newUserData = newAccountData?.user;

      const shouldShowOnboarding =
        !skipOnboarding &&
        newUserData &&
        shouldShowOnboardingAfterAuth(newUserData, contextArea);

      // Check authMethod from result or from options (backwards compatibility)
      const authMethod = result.authMethod || result.options?.authMethod;
      if (authMethod === AuthType.signup) {
        setFunnelConfig({
          type: FUNNEL_TYPES.changeUsername,
          context: {
            ...context,
            ...result.context,
            navigationContext,
            modalContext: contextArea,
          },
          options: {
            ...options,
            ...result.options,
            trapFocus: true,
            onSuccess: (usernameResult) => {
              const nextResult: FunnelOnSuccessResult = {
                ...result,
                ...usernameResult,
                data: usernameResult.data ?? result.data,
                context: {
                  ...result.context,
                  ...usernameResult.context,
                },
                options: {
                  ...result.options,
                  ...usernameResult.options,
                },
              };

              // Check if we should show onboarding after username change
              if (shouldShowOnboarding) {
                setFunnelConfig({
                  type: FUNNEL_TYPES.onboarding,
                  context: {
                    ...context,
                    ...nextResult.context,
                    navigationContext,
                    modalContext: contextArea,
                  },
                  options: {
                    ...options,
                    ...nextResult.options,
                    trapFocus: true,
                    newOnboarding: true,
                    onSuccess: handleOnboardingSuccess,
                  },
                });
              } else {
                // Skip onboarding, go directly to completion
                onComplete?.(nextResult);
              }
            },
          },
        });
      } else {
        // No onboarding needed, complete the action
        onComplete?.(result);
      }
    },
    [
      accountData,
      contextArea,
      navigationContext,
      onComplete,
      skipOnboarding,
      context,
      options,
      setFunnelConfig,
    ],
  );

  const handleOnboardingSuccess = useCallback(
    (result: FunnelOnSuccessResult) => {
      if (shouldDisablePostAuthPromosForE2E()) {
        onComplete?.(result);
        return;
      }

      const newAccountResponse = result.data?.[0];
      const userData = newAccountResponse?.data?.payload?.results;
      const hasContentContinuation = !!context?.purchaseRelease;

      if (hasContentContinuation) {
        onComplete?.(result);
        return;
      }

      // Handle next step based on backend flag
      if (userData?.display_subscription_offer_popup) {
        setFunnelConfig({
          type: FUNNEL_TYPES.subscription,
          context: {
            ...context,
            navigationContext: introOfferNavigationContext,
            modalContext: FunnelContextArea.offerPopUp,
          },
          options: {
            ...options,
            trapFocus: true,
            onSuccess: handleSubscriptionSuccess,
            onClose: () => handleSubscriptionSuccess(result),
          },
        });
      } else {
        // Show congrats modal
        setFunnelConfig({
          type: FUNNEL_TYPES.congrats,
          context: {
            ...context,
            navigationContext,
            modalContext: FunnelContextArea.offerPopUp,
          },
          options: {
            ...options,
            trapFocus: true,
            onSuccess: onComplete,
          },
        });
      }
    },
    [
      navigationContext,
      introOfferNavigationContext,
      onComplete,
      context,
      options,
      setFunnelConfig,
    ],
  );

  const handleSubscriptionSuccess = useCallback(
    (result: FunnelOnSuccessResult) => {
      setFunnelConfig({
        type: FUNNEL_TYPES.congrats,
        context: {
          ...context,
          ...result.context,
          navigationContext,
          modalContext: FunnelContextArea.offerPopUp,
        },
        options: {
          ...options,
          ...result.options,
          trapFocus: true,
          onSuccess: onComplete,
        },
      });
    },
    [navigationContext, onComplete, context, options, setFunnelConfig],
  );

  return { handleAuthSuccess, handleSubscriptionSuccess };
}
